import { DefaultWorkerPool } from "../../../file-loaders/workers/WorkerPool";
import { BVH } from "../BVHBuilder";
import { DEFAULT_CONSOLIDATION_POLYCOUNT_LIMIT } from "../../globals";
import { useEarlyConsolidation } from "../consolidation/Consolidation";
import { getGeomAndMaterialIDs } from "../consolidation/NodeConsolidation";
import { INVALID_GEOM_ID } from "../../globals";

/**
 * Compute BVH optimized for OutOfCoreTileManager.
 *
 * @param {RenderModel} model                  - The model to compute the BVH for
 * @returns {Promise<BVH>}                     - The computed BVH
 */
export async function computeOutOfCoreBVH(model) {
  // We actually don't need to compute a BVH, if it is already available or in progress.
  let bvh = model.getIterator().getBVH?.();
  if (bvh && model.getIterator().wantOoctm())
  return bvh;

  const existingJob = DefaultWorkerPool.findJob((job) => job.operation === "COMPUTE_BVH" && job.data.modelId === model.id);
  if (existingJob) {
    return (await existingJob.promise).bvhObject;
  }

  const bvhOptions = model.getData().loadOptions.bvhOptions || Autodesk.Viewing.Viewer3D._getDefaultBVHOptions();

  const fl = model.getFragmentList();

  // We are copying the data from the fragment list into a format that can be passed to a worker
  // This is necessary because the fragment list is not available in the worker
  // This creates some temporary memory overhead though.
  let { geomIds, materialIds } = getGeomAndMaterialIDs(model);

  const fragments = {
    boxes: fl.fragments.boxes,
    polygonCounts: new Uint32Array(fl.fragments.length),
    flags: new Uint8Array(fl.fragments.length),
    materials: materialIds,
    length: fl.fragments.length,
    geomids: geomIds
  };

  const polyCountLimit = bvhOptions?.consolidation_polycount_limit ?? DEFAULT_CONSOLIDATION_POLYCOUNT_LIMIT;
  const materialIdMap = model.getFragmentList().materialIdMap;
  for (let fragId = 0; fragId < fl.fragments.length; ++fragId) {
    if (useEarlyConsolidation(model)) {
      let geomId = model.loader.svf.fragments.geomDataIndexes[fragId];
      const extraFragInfo = model.loader.svf.extraFragInfo;

      fragments.polygonCounts[fragId] = geomId !== INVALID_GEOM_ID ?
      extraFragInfo.getPolygonCount(fragId) :
      0;

      if (fragments.polygonCounts[fragId] < polyCountLimit) {
        fragments.flags[fragId] = 1; // can be consolidated
      }

      fragments.flags[fragId] |= extraFragInfo.isTransparent(fragId) ? 2 : 0;
    } else {
      const geom = fl.getGeometry(fragId);
      fragments.polygonCounts[fragId] = geom ? geom.polyCount : 0;

      if (fragments.polygonCounts[fragId] < polyCountLimit) {
        fragments.flags[fragId] = 1; // can be consolidated
      }

      const materialDef = materialIdMap && materialIdMap[fragments.materials[fragId]];
      fragments.flags[fragId] |= materialDef && materialDef.transparent ? 2 : 0;
    }
  }

  const resultData = await DefaultWorkerPool.enqueueJob("COMPUTE_BVH", { fragments, modelId: model.id, bvhOptions }, [fragments.polygonCounts.buffer, fragments.flags.buffer]);
  const result = resultData.bvh;
  bvh = new BVH(result.nodes.nodesRaw, result.primitives, bvhOptions, result.fragId2nodeId, result.nodeParents);

  // Store the BVH object in the result data, so we can return the same object if another call is made for the same model.
  resultData.bvhObject = bvh;

  return bvh;
}